import { useState } from 'react';
import { requests, type Message } from '../lib/api.ts';

interface Props {
  readonly message: Message;
  readonly categoryId: string;
  readonly onDone: () => void;
}

/**
 * Turn a notice into a tracked request.
 *
 * A site's response to a notice is usually "understood" (the receipt)
 * or "understood, personally" (the ack). Only "I have a problem with
 * this" needs following up -- and that is exactly a request: status,
 * assignee, SLA, escalation.
 *
 * Keeps the board a board. Replies on notices would turn it into the
 * feed that buries the morning's instruction by noon.
 */
export const RaiseRequest = ({ message, categoryId, onDone }: Props) => {
  const [title, setTitle] = useState(message.body.slice(0, 80));
  const [body, setBody] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      await requests.file({
        categoryId,
        title: title.trim(),
        // The notice travels with the request, so a supervisor sees
        // what the site is responding to without hunting for it.
        // Raising a request about a reply would otherwise nest the
        // quote: About: "About: "...""  -- unreadable after two hops.
        body: message.body.startsWith('About:')
          ? body.trim()
          : `About: "${message.body}"\n\n${body.trim()}`,
        priority: urgent ? 'urgent' : 'normal',
      });
      onDone();
    } catch {
      setError('Could not raise that. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="resolveBox">
      <p className="mono">Raise a ticket about this notice</p>

      {error && <p className="err">{error}</p>}

      <input
        value={title}
        maxLength={160}
        placeholder="Short summary"
        onChange={(e) => setTitle(e.target.value)}
      />
      <textarea
        rows={3}
        autoFocus
        value={body}
        placeholder="What is the problem?"
        onChange={(e) => setBody(e.target.value)}
      />

      <div className="noticeRow">
        <label className="check">
          <input
            type="checkbox"
            checked={urgent}
            onChange={(e) => setUrgent(e.target.checked)}
          />
          Urgent
        </label>
        <button
          className="primary"
          disabled={busy || !title.trim() || body.trim().length < 3}
          onClick={send}
        >
          {busy ? 'Sending...' : 'Raise ticket'}
        </button>
        <button onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
};
